<template>
  <div class="review" :class="{ 'review--deleted': isDeleted }">
    <div class="d-flex items-center">
      <v-checkbox
        v-if="canBulkReplySelect"
        :inputValue="isReviewSelected"
        :disabled="checkboxDisabled"
        @change="toggleReviewSelect"
        class="mt-1 mr-2"
      ></v-checkbox>
      <v-avatar
        :color="isAnonymized ? 'transparent' : 'indigo'"
        size="36"
        class="mr-6"
      >
        <img
          v-if="!isAnonymized"
          :src="item.reviewer.profilePhotoUrl"
          :alt="item.reviewer.displayName"
        />
        <img
          v-else
          src="@/static/avatars/av.png"
          :alt="$t('message.reviews.userDataHidden')"
        />
      </v-avatar>
      <div class="relative border-right d-flex flex-column flex-fill pr-4">
        <div class="d-flex justify-space-between align-center review__name">
          <template v-if="!isAnonymized">
            {{ item.reviewer.displayName }}
          </template>
          <template v-else>
            {{ $t("message.reviews.userDataHidden") }}
          </template>
          <div class="review-actions">
            <v-tooltip
              v-if="item.rb_review_matched && showReviewAddedWithRb"
              max-width="300px"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex" v-on="on" v-bind="attrs">
                  <ReviewFromRbIcon />
                </div>
              </template>
              <span>{{
                $t("message.reviewBooster.reviews.reviewAddedWithRb")
              }}</span>
            </v-tooltip>
            <div class="review-actions__icon" @click="modifyNote(item.id)">
              <div v-if="note" class="review-actions__alert"></div>
              <v-tooltip max-width="300px" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="24px" v-bind="attrs" v-on="on" color="primary">
                    icon-sticky-note
                  </v-icon>
                </template>
                <span>{{ note ? note : $t("message.notes.addNote") }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>

        <div class="d-flex justify-start align-center mb-3 -ml-2 relative">
          <StarsRating :rating="item.rating" />
          <p class="mb-0 ml-2 grey--text text--lighten-1 review__date">
            {{
              `${$t("message.reviews.addedLabel")}: ${parseDate(
                item.gmb_create_time
              )}`
            }}
            <template v-if="isCreateAndUpdateDateDifferent">
              ({{
                `${$t("message.common.edited")}: ${parseDate(
                  item.gmb_update_time
                )}`
              }})
            </template>
          </p>
          <div v-if="isDeleted || isAccurate" class="review__labels">
            <div v-if="isAccurate" class="d-flex align-center">
              <p class="review__label review__label--accurate">
                {{ $t("message.reviews.accurateLabel") }}
              </p>
              <v-tooltip top max-width="350">
                <template v-slot:activator="{ on }">
                  <v-icon small color="primary" v-on="on" class="tooltip">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>{{ $t("message.reviews.accurateTooltip") }}</span>
              </v-tooltip>
            </div>
            <p v-if="isDeleted" class="review__label review__label--deleted">
              {{ $t("message.reviews.deletedLabel") }}
            </p>
          </div>
        </div>

        <p
          class="review__comment"
          :class="{
            'review__comment--line': item.reply_comment,
            'review__comment--missing': !item.comment
          }"
        >
          <v-icon v-if="!item.comment" class="review__comment-icon">
            mdi-information
          </v-icon>
          <span v-html="`${itemContent}${textEllipsis}`"></span>
          <span
            @click="showMore = true"
            class="font-weight-bold cursor-pointer"
            v-if="item.comment && item.comment.length > 190 && !showMore"
          >
            {{ $t("message.reviews.more") }}
          </span>
        </p>

        <div
          v-if="
            (item.reply_comment || item.reply_comment_being_sent) && !answering
          "
          class="review__reply"
          :class="{
            'review__reply--disabled': isReplySendError
          }"
        >
          <div
            v-if="isReplySendError"
            class="review__reply--processing is-error"
          >
            <v-icon>
              mdi-alert-circle-outline
            </v-icon>
            {{ $t("message.reviews.replyTemplates.replyError") }}
          </div>
          <v-avatar
            class="absolute-avatar"
            :class="{
              'absolute-avatar--disabled': isReplySendError
            }"
            color="indigo"
            size="36"
          >
            <v-img src="@/static/avatars/av-primary.png" alt="avatar" />
          </v-avatar>
          <p class="review__name">{{ $t("message.reviews.owner") }}</p>
          <p
            class="mb-2 grey--text text--lighten-1 review__date"
            v-if="item.reply_update_time"
          >
            {{
              `${$t("message.reviews.answer")}: ${parseDate(
                item.reply_update_time
              )}`
            }}
          </p>

          <p
            v-html="
              isQueued
                ? item.reply_comment_being_sent
                : item.reply_comment || item.reply_comment_being_sent
            "
          ></p>
        </div>

        <div
          v-if="!isProcessing && !item.reply_comment && canReply"
          class="reply-actions"
        >
          <ui-button
            @click="answering = !answering"
            color="primary"
            outlined
            icon="mdi-reply"
          >
            {{ $t("message.reviews.answerBtn") }}
          </ui-button>
          <ReviewAiButton
            v-if="canUseAiReviewReply"
            :is-ai-button-disabled="isAiButtonDisabled"
            :disabled-ai-button-tooltip="disabledAiButtonTooltip"
            :label="$t('message.reviews.aiReply.replyWithAi')"
            icon="icon-ai-generate"
            @ai-reply="aiReplyHandler"
          />

          <ui-button
            v-if="showReplyTemplates && canUseReviewTemplates"
            @click="dialog.chooseTemplate = true"
            icon="icon-template"
          >
            {{ $t("message.reviews.replyTemplates.useTemplate") }}
          </ui-button>
        </div>
        <div v-if="showAiReply" class="ai-reply">
          <v-textarea
            name="aiAnswer"
            :loading="inputLoading"
            v-model="answer"
            :label="$t('message.reviews.yourAnswer')"
            v-validate="'required'"
            :error-messages="errors.collect('aiAnswer')"
            rows="1"
            auto-grow
            color="primary"
          ></v-textarea>
          <div class="ai-reply__actions">
            <ReviewAiButton
              v-if="showAiReply"
              @ai-reply="aiReplyHandler"
              :is-ai-button-disabled="isAiButtonDisabled"
              :disabled-ai-button-tooltip="disabledAiButtonTooltip"
              :label="$t('message.reviews.aiReply.generateAgain')"
              icon="icon-ai-regenerate"
            />

            <ui-button
              @click="handleReply"
              color="primary"
              :loading="isAiReplyInProgress"
            >
              {{ $t("message.reviews.publishAnswer") }}
            </ui-button>
            <ui-button
              outlined
              transparent
              color="primary"
              @click="cancelAnswer"
            >
              {{ $t("message.reviews.cancel") }}</ui-button
            >
          </div>
        </div>
        <div v-if="answering" class="pr-4">
          <v-textarea
            name="answer"
            :loading="inputLoading"
            v-model="answer"
            :label="$t('message.reviews.yourAnswer')"
            v-validate="'required'"
            :error-messages="errors.collect('answer')"
            rows="1"
            auto-grow
            color="primary"
          ></v-textarea>
          <div class="ai-reply__actions">
            <ui-button
              @click="handleReply"
              :loading="isAiReplyInProgress"
              color="primary"
            >
              {{ $t("message.reviews.publishAnswer") }}
            </ui-button>
            <ui-button
              outlined
              transparent
              color="primary"
              @click="cancelAnswer"
            >
              {{ $t("message.reviews.cancel") }}</ui-button
            >
          </div>
        </div>
        <div
          v-if="item.reply_comment && !answering && canReplyToReviewsManually"
        >
          <v-tooltip
            :disabled="!item.is_optimistic && !isProcessing"
            bottom
            max-width="300px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                color="primary"
                small
                v-bind="attrs"
                v-on="on"
                @click="
                  item.is_optimistic || isProcessing ? null : handleEditReply()
                "
              >
                <v-icon size="21" class="mr-2">icon-edit</v-icon>
                {{
                  !isReplySendError
                    ? $t("message.reviews.edit")
                    : $t("message.reviews.editAndResend")
                }}
              </v-btn>
            </template>
            <span>
              {{ $t("message.reviews.cannotEditOptimisticReply") }}
            </span>
          </v-tooltip>

          <v-tooltip
            v-if="!isReplySendError"
            :disabled="!item.is_optimistic && !isProcessing"
            bottom
            max-width="300px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                prepend-icon="icon-calendar"
                small
                text
                color="red-1"
                v-bind="attrs"
                v-on="on"
                @click="
                  item.is_optimistic || isProcessing
                    ? null
                    : (dialog.confirmation = true)
                "
              >
                <v-icon class="mr-2">icon-close</v-icon>
                {{ $t("message.reviews.delete") }}
              </v-btn>
            </template>
            <span>
              {{ $t("message.reviews.cannotDeleteOptimisticReply") }}
            </span>
          </v-tooltip>
        </div>
      </div>
      <div class="reply-info">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-3" color="primary">icon-owner</v-icon>
          <div>
            <router-link
              class="gmb-name"
              :to="{
                name: 'profile-facility-details',
                params: { id: item.facility_id }
              }"
            >
              {{ item.gmb_address_name }}
            </router-link>
            <p class="mb-0 gmb-address">
              {{ storeCode }}
            </p>
          </div>
        </div>
        <slot name="tags"> </slot>
      </div>
    </div>
    <ConfirmationDialog
      :isOpen="dialog.confirmation"
      :body="confirmationDialogBody"
      @closeDialog="dialog.confirmation = false"
      @delete="handleDeleteReply"
    />

    <ChooseReplyTemplate
      v-if="canUseReviewTemplates"
      :isOpen="dialog.chooseTemplate"
      @useTemplate="useTemplate"
      @close="dialog.chooseTemplate = false"
    />
    <ModifyNoteDialog
      v-if="showModifyDialog"
      :note="note"
      :is-open="showModifyDialog"
      :review-id="item.id"
      @change="onNoteChange"
      @close="showModifyDialog = false"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import ConfirmationDialog from "@/components/ListingsConfiguration/Dialog/Confirmation";
import ChooseReplyTemplate from "@/components/Reviews/ReplyTemplates/ChooseReplyTemplate";
import StarsRating from "@/components/ui/StarsRating";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { REVIEWS_REPLY_STATUS } from "@/enums/reviews-reply-status.enum";
import reviewsService from "@/services/reviews";
import ReviewAiButton from "./ReviewAiButton.vue";
import ReviewFromRbIcon from "@/components/ui/Icons/ReviewFromRbIcon.vue";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "ReviewItem",
  components: {
    ConfirmationDialog,
    ChooseReplyTemplate,
    StarsRating,
    ReviewAiButton,
    ReviewFromRbIcon,
    ModifyNoteDialog: () => import("@/components/Reviews/ModifyNoteDialog")
  },
  mixins: [permissionsInfo],
  inject: {
    showReviewAddedWithRb: {
      default: false
    },
    showReplyTemplates: {
      default: false
    }
  },
  props: {
    item: {
      required: true,
      type: Object
    },
    aiLimits: {
      required: true,
      type: Object
    },
    isReviewSelected: {
      type: Boolean,
      required: true
    },
    checkboxDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      REVIEWS_REPLY_STATUS,
      answering: false,
      showAiReply: false,
      answer: "",
      aiAnswer: "",
      inputLoading: false,
      showMore: false,
      dialog: {
        confirmation: false,
        chooseTemplate: false
      },
      confirmationDialogBody: {
        isError: false,
        title: this.$t("message.reviews.deleteReplyConfirm"),
        text: "",
        hasCancelButton: true,
        hasDeleteButton: true
      },
      responseHttpCode: null,
      showModifyDialog: false,
      localNote: null,
      aiGenerated: false,
      stream: null
    };
  },
  computed: {
    canReplyToReviewsManually() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_REPLY_TO_REVIEWS_MANUALLY
      );
    },
    showCheckboxWhenReplied() {
      return isFeatureFlagEnabled(
        FEATURE_FLAG.CN_6968_BULK_ASSIGN_CATEGORIES_FOR_REPLIED_REVIEWS
      );
    },
    canUseAiReviewReply() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_AI_REVIEW_REPLY);
    },
    canUseReviewTemplates() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_REVIEW_TEMPLATES);
    },
    canUseAiReviewCategorization() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_USE_AI_REVIEWS_CATEGORIZATION
      );
    },
    ...mapGetters({
      getBusinessAddressById: "businessAddresses/getBusinessAddressById"
    }),
    isAiButtonDisabled() {
      return (
        this.isAiReplyInProgress ||
        this.isOnlyRateAdded ||
        !(this.aiLimits && this.aiLimits.remaining_limit)
      );
    },
    disabledAiButtonTooltip() {
      return !this.aiLimits.remaining_limit && !this.isOnlyRateAdded
        ? this.$t("message.reviews.aiReply.outOfLimitTooltip")
        : this.$t("message.reviews.aiReply.onlyTextTooltip");
    },
    isCreateAndUpdateDateDifferent() {
      return (
        this.parseDate(this.item.gmb_create_time) !==
        this.parseDate(this.item.gmb_update_time)
      );
    },
    note() {
      return this.localNote;
    },
    isAiReplyInProgress() {
      return this.$store.state.reviews.isAiReplyInProgress;
    },
    canReply() {
      return (
        !this.answering &&
        !this.showAiReply &&
        this.canReplyReviews &&
        this.canReplyToReviewsManually
      );
    },
    isProcessing() {
      return (
        this.item.processing_in_progress || !!this.item.reply_comment_being_sent
      );
    },
    isQueued() {
      return this.responseHttpCode === 202;
    },
    isReplySendError() {
      return this.item.status === REVIEWS_REPLY_STATUS.ERROR;
    },
    isAnonymized() {
      return this.item.disabled_collecting_personal_data;
    },
    isDeleted() {
      return this.item && this.item.is_deleted;
    },
    isAccurate() {
      return this.item && this.item.is_accurate;
    },
    canBulkReplySelect() {
      return (
        !this.isProcessing &&
        !this.isDeleted &&
        (!this.item.reply_comment || this.showCheckboxWhenReplied)
      );
    },
    isOnlyRateAdded() {
      return !this.item.comment;
    },
    itemContent() {
      return this.item.comment
        ? this.item.comment.substring(
            0,
            !this.showMore ? 190 : this.item.comment.length
          )
        : this.$t("message.reviews.noComment");
    },
    textEllipsis() {
      return this.item.comment &&
        this.item.comment.length > 190 &&
        !this.showMore
        ? "..."
        : "";
    },
    storeCode() {
      const storeLabel =
        "store_code" in this.item && this.item.store_code
          ? ` | ${this.$t("message.common.storeCode")}: ${this.item.store_code}`
          : "";
      return `${this.item.formatted_address}${storeLabel}`;
    }
  },
  created() {
    this.localNote = this.item.note ? this.item.note.text : null;
  },
  methods: {
    async aiReplyHandler() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_REPLY_GENERATED_AI,
        gmbaddress_id: this.item ? this.item.gmb_address : null,
        review_id: this.item ? this.item.id : null
      });
      if (!this.answering) {
        this.showAiReply = true;
      }
      this.$store.dispatch("reviews/setIsAiReplyInProgress", true);
      const {
        user_id: userId
      } = this.$store.state.company.company.company_user;

      if (this.stream) {
        this.stream.close();
        this.stream = null;
      }

      this.stream = reviewsService.getAiReply({
        companyId: this.$store.state.company.company.id,
        gmbReviewId: this.item.id,
        userId
      });

      this.stream.connect();

      await this.stream
        .onMessage(message => {
          this.aiAnswer = message;
          this.answer = message;
        })
        .catch(error => {
          this.aiErrorHandler();
        })
        .finally(() => {
          this.$emit("ai-reply");
          this.stream.close();
          this.stream = null;
          this.$store.dispatch("reviews/setIsAiReplyInProgress", false);
        });
    },
    aiErrorHandler() {
      this.$notify({
        group: "default",
        type: "error",
        text: this.$t("message.reviews.aiReply.error")
      });
    },
    ...mapActions({
      postReply: "reviews/postReply",
      deleteReply: "reviews/deleteReply"
    }),
    onNoteChange(note) {
      this.localNote = note;
      this.showModifyDialog = false;
    },
    moment() {
      return moment();
    },
    parseDate(date) {
      return moment(date).format("DD.MM.YYYY");
    },
    cancelAnswer() {
      this.showAiReply = null;
      this.answering = false;
      this.$nextTick(() => {
        this.answer = null;
      });
    },

    async handleReply() {
      if (this.isReplyInvalid()) {
        return;
      }

      this.inputLoading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_REPLY,
        review_id: this.item ? this.item.id : null,
        gmbaddress_id: this.item ? this.item.gmb_address : null,
        ...(this.aiAnswer && {
          reply_text_generated_ai: this.aiAnswer,
          reply_text_used: this.answer
        })
      });

      try {
        this.item.reply_comment = this.answer;
        this.item.is_optimistic = true;
        this.answering = false;
        this.showAiReply = false;
        const { status } = await this.postReply({
          id: this.item.id,
          reply: this.answer
        });
        this.responseHttpCode = status;
        this.answering = false;
        this.showAiReply = false;
        this.updated("updated", this.item.id);
        this.item.is_optimistic = false;
        this.$emit("reply-sent", this.item.id);
      } catch (error) {
        const answerField = this.answering ? "answer" : "aiAnswer";
        this.addValidationErrors(
          answerField,
          error.response.data.reply_comment
        );
        this.item.reply_comment = "";
        this.item.is_optimistic = false;
      }
      this.inputLoading = false;
    },
    useTemplate(template) {
      this.answering = true;
      this.answer = template.text;

      // GTM event data collection below
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_TEMPLATE_USE,
        template_id: template ? template.id : null,
        review_id: this.item ? this.item.id : null
      });
    },
    handleEditReply() {
      this.answering = true;

      this.answer = this.item.reply_comment;
      this.updated("updated", this.item.id);
    },
    async handleDeleteReply() {
      this.dialog.confirmation = false;
      this.inputLoading = true;
      await this.deleteReply({
        id: this.item.id
      });
      this.inputLoading = false;
      this.updated(this.item.id);
      this.$emit("reply-deleted", this.item.id);
    },

    toggleReviewSelect() {
      this.$emit("toggleReviewSelect", this.item.id);
    },

    updated(id) {
      this.$emit("updated", id);
    },
    modifyNote() {
      this.showModifyDialog = true;
    },
    addValidationErrors(field, errors) {
      errors.forEach(error => {
        this.errors.add({
          field: field,
          msg: error
        });
      });
    },
    isReplyInvalid() {
      this.$validator.validate();

      return (
        (this.answering && this.errors.has("answer")) ||
        (this.showAiReply && this.errors.has("aiAnswer"))
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.review-actions {
  display: flex;
  gap: $spacing * 2;
  align-items: center;

  &__icon {
    cursor: pointer;
    position: relative;
  }

  &__alert {
    width: 6px;
    height: 6px;
    position: absolute;
    z-index: 1;
    top: $spacing * 0.2;
    right: $spacing * 0.1;
    background: $pink;
    border-radius: 100%;
  }
}
.ai-reply {
  &__actions {
    display: flex;
    gap: $spacing;
  }
}
.gmb-name {
  font-weight: 600;
  @include font-size(12px);
  line-height: 16px;
  color: $dark-blue-3;
  text-decoration: none;
  cursor: pointer;
  margin-bottom: 0;

  &:hover {
    text-decoration: underline;
  }
}

.gmb-address {
  @include font-size(12px);
  line-height: 16px;
  font-weight: normal;
  color: $dark-blue-3;
}

.absolute-avatar {
  position: absolute;
  left: -60px;

  &--disabled {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $light;
      opacity: 0.5;
    }
  }
}

.relative {
  position: relative;
}

.review {
  position: relative;
  padding: $spacing * 2.5 0;
  border-bottom: 1px solid $base-light-2;
  margin: 0;

  .border-right {
    border-right: 1px solid $base-light-2;
  }

  &__labels {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    align-items: flex-end;
    z-index: 10;

    &:not(:last-child) {
      margin-right: $spacing;
    }

    .tooltip {
      margin-left: $spacing * 0.5;
    }
  }

  &__label {
    margin-bottom: 0;
    padding: $spacing * 0.5 $spacing * 2;
    border-radius: $spacing * 2;
    color: white;
    @include font-size(12px);
    font-weight: 400;

    &--deleted {
      border: 1px solid $pink;
      background-color: $pink;
      margin-left: $spacing * 0.5;
    }

    &--accurate {
      border: 1px solid $primary;
      color: $primary;
    }
  }

  &__date {
    @include font-size(14px);
  }

  &__comment {
    @include font-size(14px);
    line-height: 20px;
    max-width: 680px;
    position: relative;

    &--line {
      &:before {
        content: "";
        position: absolute;
        left: -43px;
        top: -5px;
        bottom: 0;
        width: 1px;
        background-color: $gray-1;
      }
    }

    &--missing {
      @include font-size(12px);
      color: $gray-light;
      font-weight: 400;
      line-height: 17px;
    }
  }

  &__comment-icon {
    color: $gray-300;
    margin-right: $spacing;
  }

  &__reply {
    position: relative;

    &--disabled {
      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: $light;
        opacity: 0.5;
      }
    }

    &--processing {
      position: absolute;
      display: flex;
      align-items: center;
      right: $spacing;
      top: $spacing;
      z-index: 2;

      p {
        margin-bottom: 0;
        @include font-size(14px);
      }

      &.is-error {
        @include font-size(14px);
        ::v-deep.v-icon {
          color: $red-1;
          @include font-size(22px);
          margin-right: $spacing * 0.5;
        }
      }
    }
  }

  &__name {
    font-weight: 600;
    @include font-size(15px);
    line-height: 18px;
    margin-bottom: 5px;
  }

  &--deleted {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $light;
      opacity: 0.5;
    }
  }

  ::v-deep .v-text-field__details {
    margin-bottom: $spacing;
  }
}

.-ml-2 {
  margin-left: -5px;
}

.cursor-pointer {
  cursor: pointer;
}

.reply-info {
  max-width: 582px;
  width: 100%;
  flex: 0 0 40%;
  padding-left: $spacing * 4;
  position: relative;
}

.reply-actions {
  display: flex;
  flex-wrap: wrap;
  margin: $spacing * -0.75 0;
  gap: $spacing;

  ::v-deep .v-btn {
    margin-top: $spacing * 0.75;
    margin-bottom: $spacing * 0.75;
  }
}
</style>
